/**
 * NOS-57: Hooks for form submissions inbox.
 * useFormSubmissions, useUpdateSubmissionStatus, useUnreadSubmissionsCount
 */

import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import { errorMessage } from "@/lib/errorMessage"
import { useOrgStore } from "@/stores/orgStore"

export type FormSubmissionStatus = "new" | "read" | "replied" | "archived"

export interface FormSubmissionWithContact {
  id: string
  org_id: string
  contact_id: string | null
  page_section_id: string | null
  form_data: Record<string, unknown>
  created_at: string
  status: FormSubmissionStatus
  read_at: string | null
  contact_name: string | null
  contact_email: string | null
  page_title: string | null
}

export interface FormSubmissionsFilters {
  status?: FormSubmissionStatus | "all"
  dateFrom?: string
  dateTo?: string
}

const PAGE_SIZE = 50

export function useFormSubmissions(filters?: FormSubmissionsFilters) {
  const { currentOrg, userOrgs } = useOrgStore()
  const orgId = (currentOrg ?? userOrgs[0])?.id

  const [submissions, setSubmissions] = useState<FormSubmissionWithContact[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSubmissions = useCallback(async () => {
    if (!orgId) {
      setSubmissions([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      let query = supabase
        .from("form_submissions")
        .select(
          "id, org_id, contact_id, page_section_id, form_data, created_at, status, read_at"
        )
        .eq("org_id", orgId)
        .order("created_at", { ascending: false })
        .limit(PAGE_SIZE)

      const statusFilter = filters?.status
      if (statusFilter && statusFilter !== "all") {
        query = query.eq("status", statusFilter)
      }

      const dateFrom = filters?.dateFrom
      const dateTo = filters?.dateTo
      if (dateFrom) {
        query = query.gte("created_at", `${dateFrom}T00:00:00Z`)
      }
      if (dateTo) {
        query = query.lte("created_at", `${dateTo}T23:59:59.999Z`)
      }

      const { data: rows, error: fetchError } = await query

      if (fetchError) {
        setError(fetchError.message)
        setSubmissions([])
        return
      }

      const submissionRows = (rows ?? []) as Array<{
        id: string
        org_id: string
        contact_id: string | null
        page_section_id: string | null
        form_data: Record<string, unknown>
        created_at: string
        status: FormSubmissionStatus
        read_at: string | null
      }>

      const contactIds = [
        ...new Set(
          submissionRows.map((r) => r.contact_id).filter((id): id is string => !!id)
        ),
      ]
      const pageSectionIds = [
        ...new Set(
          submissionRows
            .map((r) => r.page_section_id)
            .filter((id): id is string => !!id)
        ),
      ]

      const contactsMap = new Map<string, { name: string | null; email: string }>()
      const pageTitleMap = new Map<string, string>()

      if (contactIds.length > 0) {
        const { data: contactsData } = await supabase
          .from("contacts")
          .select("id, name, email")
          .in("id", contactIds)
        ;(contactsData ?? []).forEach(
          (c: { id: string; name: string | null; email: string }) => {
            contactsMap.set(c.id, { name: c.name, email: c.email })
          }
        )
      }

      if (pageSectionIds.length > 0) {
        const { data: sectionsData } = await supabase
          .from("page_sections")
          .select("id, page_id")
          .in("id", pageSectionIds)
        const pageIds = [
          ...new Set(
            (sectionsData ?? []).map((s: { id: string; page_id: string }) => s.page_id)
          ),
        ]
        if (pageIds.length > 0) {
          const { data: pagesData } = await supabase
            .from("pages")
            .select("id, title")
            .in("id", pageIds)
          const pageTitleById = new Map(
            (pagesData ?? []).map((p: { id: string; title: string | null }) => [
              p.id,
              p.title ?? "Contact Form",
            ])
          )
          ;(sectionsData ?? []).forEach(
            (s: { id: string; page_id: string }) => {
              pageTitleMap.set(s.id, pageTitleById.get(s.page_id) ?? "Contact Form")
            }
          )
        }
      }

      const mapped: FormSubmissionWithContact[] = submissionRows.map((r) => {
        const contact = r.contact_id ? contactsMap.get(r.contact_id) : null
        const pageTitle = r.page_section_id
          ? pageTitleMap.get(r.page_section_id) ?? null
          : null
        return {
          id: r.id,
          org_id: r.org_id,
          contact_id: r.contact_id,
          page_section_id: r.page_section_id,
          form_data: r.form_data ?? {},
          created_at: r.created_at,
          status: r.status,
          read_at: r.read_at,
          contact_name: contact?.name ?? null,
          contact_email: contact?.email ?? null,
          page_title: pageTitle,
        }
      })

      setSubmissions(mapped)
    } catch (err) {
      setError(errorMessage(err, "Failed to fetch submissions"))
      setSubmissions([])
    } finally {
      setLoading(false)
    }
  }, [orgId, filters?.status, filters?.dateFrom, filters?.dateTo])

  useEffect(() => {
    fetchSubmissions()
  }, [fetchSubmissions])

  return { submissions, loading, error, refetch: fetchSubmissions }
}

export function useUpdateSubmissionStatus() {
  const { currentOrg, userOrgs } = useOrgStore()
  const orgId = (currentOrg ?? userOrgs[0])?.id

  const updateStatus = useCallback(
    async (
      submissionId: string,
      status: FormSubmissionStatus
    ): Promise<{ success: boolean; error?: string }> => {
      if (!orgId) return { success: false, error: "No org selected" }

      const updates: Record<string, unknown> = { status }
      if (status === "read") {
        updates.read_at = new Date().toISOString()
      }

      const { error } = await supabase
        .from("form_submissions")
        .update(updates)
        .eq("id", submissionId)
        .eq("org_id", orgId)

      if (error) {
        return { success: false, error: error.message }
      }
      return { success: true }
    },
    [orgId]
  )

  return { updateStatus }
}

export function useUnreadSubmissionsCount() {
  const { currentOrg, userOrgs } = useOrgStore()
  const orgId = (currentOrg ?? userOrgs[0])?.id

  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const fetchCount = useCallback(async () => {
    if (!orgId) {
      setCount(0)
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const { count: c, error } = await supabase
        .from("form_submissions")
        .select("*", { count: "exact", head: true })
        .eq("org_id", orgId)
        .eq("status", "new")

      if (error) {
        setCount(0)
        return
      }
      setCount(c ?? 0)
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => {
    fetchCount()
  }, [fetchCount])

  // Poll every 30s for live updates
  useEffect(() => {
    if (!orgId) return
    const interval = setInterval(fetchCount, 30000)
    return () => clearInterval(interval)
  }, [orgId, fetchCount])

  return { count, loading, refetch: fetchCount }
}
